import AttributesSelector from '@Alternatives/Attributes/Components/AttributesSelector.vue';
import { DownloadBlob } from "@browser/Browser";
import { getTimestamp, isNullOrWhitespace, strCompareLowerCase } from "@common/Common";
import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue';
import { ValidationError } from '@common/types';
import { ICommandStatus } from '@cqrs/cqrs';
import { IEvaluationCommand } from '@Evaluation/cqrs';
import { PanelAlternativesManagerSaveEventPayload, PanelAlternativesManagerViewModel } from '@Evaluation/Estimations/Components/PanelAlternativesManager';
import PanelAlternativesManager from "@Evaluation/Estimations/Components/PanelAlternativesManager.vue";
import { PanelCloserViewModel } from '@Evaluation/Estimations/Components/PanelCloser';
import PanelCloser from "@Evaluation/Estimations/Components/PanelCloser.vue";
import EstimationsPanelEditor from '@Evaluation/Estimations/Components/PanelEditor.vue';
import { PanelOpenerViewModel } from '@Evaluation/Estimations/Components/PanelOpener';
import PanelOpener from "@Evaluation/Estimations/Components/PanelOpener.vue";
import { SurveyAccessLinksViewModel } from '@Evaluation/Estimations/Components/SurveyAccessLinks';
import SurveyAccessLinks from '@Evaluation/Estimations/Components/SurveyAccessLinks.vue';
import { ClonePanelCommandV1, ClosePanelAccessCommandV1, CreatePanelCommandV1, DeletePanelCommandV1, OpenPanelAccessCommandV1, UpdateAlternativesCommandV1, UpdatePanelAlternativesSettingsCommandV1, UpdatePanelAttributesSettingsCommandV1, UpdatePanelCommandV1 } from '@Evaluation/Estimations/Domain/commands-core';
import EstimationsSurveysCommands from "@Evaluation/Estimations/Domain/commands-vue-component";
import EstimationsQueries from '@Evaluation/Estimations/Domain/queries-vue-component';
import { AlternativesViewReadModel, AttributeReadModel, DownloadReadModel, MemberReadModel, PanelistReadModel, PanelistsReadModel, PanelReadModel, PanelsPageReadModel } from '@Evaluation/Estimations/Domain/readmodels';
import Component, { mixins } from 'vue-class-component';


@Component({
    components: { PortfolioNavigation, AttributesSelector, PanelAlternativesManager, EstimationsPanelEditor, PanelOpener, PanelCloser, SurveyAccessLinks }
})
export default class PanelsPage extends mixins(EstimationsSurveysCommands, EstimationsQueries)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId() { return Number.parseInt(this.$route.params.projectId); }

    initialized: boolean = false;
    EvalModelId: number;
    TotalCriteriaCount: number;
    Surveys: PanelReadModel[] = null;
    SearchPhrase: string = null;
    WorkspaceMembers: MemberReadModel[] = [];
    FilterByOwnerEmail: string = null;
    UserEmail: string;
    UserId: number;

    AlternativesListId: number;
    AlternativeLabel: string;
    AlternativesLabel: string;
    Views: AlternativesViewReadModel[];
    Attributes: AttributeReadModel[];

    attributesManager: AttributesSelectorProps = null;
    panelEditor: PanelEditorProps = null;

    panelOpener: PanelOpenerViewModel = new PanelOpenerViewModel();
    panelCloser: PanelCloserViewModel = new PanelCloserViewModel();
    surveyAccessLinks: SurveyAccessLinksViewModel = new SurveyAccessLinksViewModel();
    panelAlternativesManager: PanelAlternativesManagerViewModel = new PanelAlternativesManagerViewModel();

    get SurveysView(): PanelReadModel[]
    {
        let view = new Array(...this.Surveys);

        if (!isNullOrWhitespace(this.SearchPhrase))
        {
            const phrase = this.SearchPhrase.toLowerCase();
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) >= 0);
        }

        if (!isNullOrWhitespace(this.FilterByOwnerEmail))
        {
            const filter = this.FilterByOwnerEmail.toLowerCase();
            view = view
                .filter(a => !isNullOrWhitespace(a.OwnerEmail))
                .filter(a => a.OwnerEmail.toLowerCase() === filter);
        }

        return view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
    }

    get FilterByOwnerLabel(): string 
    {
        if (isNullOrWhitespace(this.FilterByOwnerEmail))
        {
            return "All owners";
        }

        const member = this.WorkspaceMembers.find(a => a.Email === this.FilterByOwnerEmail);
        return member.Name;
    }


    showPanelOpener(surveyId: number)
    {
        this.panelOpener.show(surveyId);
    }

    showPanelCloser(surveyId: number)
    {
        this.panelCloser.show(surveyId);
    }


    showPanelAlternativesManager(p: PanelReadModel)
    {
        this.panelAlternativesManager.show(p.SurveyId, p.AlternativeDescriptionTemplate, p.RespondentsAttributeId, p.AlternativesViewDefinitionId, this.AlternativesLabel, this.Attributes, this.Views);
    }

    addOrUpdateSurvey(rm: PanelReadModel)
    {
        const curr = this.Surveys.find(a => a.SurveyId === rm.SurveyId);

        if (curr != null)
        {
            this.Surveys.remove(curr);
        }

        this.Surveys.push(rm);
    }

    getOwnerName(rm: PanelReadModel): string 
    {
        if (isNullOrWhitespace(rm.OwnerEmail))
        {
            return 'None';
        }

        const member = this.WorkspaceMembers.find(a => a.Email === rm.OwnerEmail);

        if (member)
        {
            return member.Name;
        }

        return rm.OwnerEmail;
    }


    setOwnerFilter(email: string)
    {
        this.FilterByOwnerEmail = email;
    }


    showAccessLinks(surveyId: number)
    {
        this.surveyAccessLinks.show(surveyId);
        this.runPanelistsQueryV1(this.workspaceId, surveyId);
    }

    showSurveyCreator()
    {
        this.panelEditor = new PanelEditorProps(0, null, null, this.WorkspaceMembers, this.UserEmail, null);
    }

    showSurveyEditor(vm: PanelReadModel)
    {
        this.panelEditor = null;
        this.panelEditor = new PanelEditorProps(vm.SurveyId, vm.Name, vm.Description, this.WorkspaceMembers, vm.OwnerEmail, vm.ResponseDueDate);
    }

    closePanelEditor()
    {
        this.panelEditor = null;
    }

    showAttributesManager(vm: PanelReadModel)
    {
        this.attributesManager = new AttributesSelectorProps(vm.SurveyId.toString(), vm.AttributesIds, this.Attributes.filter(a => a.IsAssessable));
    }

    closeAttributesManager()
    {
        this.attributesManager = null;
    }



    handleSendEmailCommand(e: any)
    {
        const recipientsIds = (e.Recipients as PanelistReadModel[]).map(a => a.ProjectUserId);
        this.invokeSendEmailCommandV1(this.workspaceId, e.SurveyId, recipientsIds, e.EmailSubject, e.EmailBody);
    }

    downloadLinks(surveyId: number)
    {
        this.runExportAccessLinksBySurveyIdQueryV1(this.workspaceId, surveyId);
    }

    downloadVotes()
    {
        this.runExportAllVotesQueryV1(this.workspaceId, this.projectId, this.EvalModelId);
    }

    downloadDisagreements()
    {
        this.runExportAllDisagreementsQueryV1(this.workspaceId, this.projectId, this.EvalModelId);
    }



    deletePanel(vm: PanelReadModel)
    {
        if (confirm(`Do you want to delete ${vm.Name}?`))
        {
            this.handleDeletePanelCommand(vm.SurveyId);
        }
    }

    handleClonePanelCommand(vm: PanelReadModel)
    {
        const cloneName = `${vm.Name} (${getTimestamp()})`;
        this.invokeClonePanelCommandV1(this.workspaceId, vm.SurveyId, cloneName);
    }

    handleUpdateAlternativesCommand(p: PanelReadModel)
    {
        p.IsSaving = true;
        this.invokeUpdateAlternativesCommandV1(this.UserId, this.workspaceId, p.SurveyId);
    }


    handleCreatePanelCommand(e: any)
    {
        this.panelEditor.clearValidationErrors();
        this.invokeCreatePanelCommandV1(this.workspaceId, this.EvalModelId, e.Name, e.Description, e.OwnerEmail, e.ResponseDueDate);
    }

    handleUpdatePanelCommand(e: any)
    {
        this.panelEditor.clearValidationErrors();
        this.invokeUpdatePanelCommandV1(this.workspaceId, e.SurveyId, this.EvalModelId, e.Name, e.Description, e.OwnerEmail, e.ResponseDueDate);
    }

    handleDeletePanelCommand(surveyId: number)
    {
        this.invokeDeletePanelCommandV1(this.workspaceId, surveyId);
    }

    handleOpenPanelAccessCommand(surveyId: number)
    {
        this.invokeOpenPanelAccessCommandV1(this.workspaceId, surveyId);
    }

    handleClosePanelAccessCommand(surveyId: number)
    {
        this.invokeClosePanelAccessCommandV1(this.workspaceId, surveyId);
    }


    handleUpdatePanelAttributesSettingsCommand(e: any)
    {
        const surveyId = Number.parseInt(e.CorrelationId);

        this.invokeUpdatePanelAttributesSettingsCommandV1(this.workspaceId, surveyId, e.SelectedAttributesIds);
    }

    handleUpdatePanelAlternativesSettingsCommand(e: PanelAlternativesManagerSaveEventPayload)
    {
        this.invokeUpdatePanelAlternativesSettingsCommandV1(this.workspaceId, e.surveyId, e.attributeId, e.viewDefId, e.descriptionTemplate);
    }

    mounted()
    {
        document.title = "Assessments | TransparentChoice";
        this.runPanelsPageQueryV1(this.projectId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName === "PanelsPageReadModel")
        {
            const rm = model as PanelsPageReadModel;

            this.EvalModelId = rm.EvaluationModelId;
            this.TotalCriteriaCount = rm.CriteriaCount;
            this.AlternativesLabel = rm.AlternativesLabel;
            this.WorkspaceMembers = rm.WorkspaceMembers;
            this.UserEmail = rm.UserEmail;
            this.UserId = rm.UserId;
            this.Views = rm.Views;
            this.Attributes = rm.Attributes;

            this.AlternativesListId = rm.AlternativesListId;
            this.AlternativeLabel = rm.AlternativeLabel;
            this.AlternativesLabel = rm.AlternativesLabel;
            this.Surveys = rm.Panels;

            this.initialized = true;
        }
        else if (modelName === "PanelReadModel")
        {
            const rm = model as PanelReadModel;
            this.addOrUpdateSurvey(rm);
            this.panelAlternativesManager.close();
        }
        else if (modelName === "DownloadReadModel")
        {
            const rm = model as DownloadReadModel;
            DownloadBlob(rm.Url);
        }
        else if (modelName === "PanelistsReadModel")
        {
            const rm = model as PanelistsReadModel;
            this.surveyAccessLinks.setData(rm.Panelists);
        }
    }

    onCommandSucceed(cmd: IEvaluationCommand, correlationId: string)
    {
        if (ClonePanelCommandV1.matchType(cmd))
        {
            const cloneName = correlationId;
            this.runPanelByNameQueryV1(this.workspaceId, this.EvalModelId, cloneName);
        }
        else if (UpdateAlternativesCommandV1.matchType(cmd))
        {
            const surveyId = Number.parseInt(correlationId);
            this.Surveys.find(a => a.SurveyId === surveyId).IsSaving = false;
        }
        else if (DeletePanelCommandV1.matchType(cmd))
        {
            const surveyId = Number.parseInt(correlationId);
            this.Surveys.remove(this.Surveys.find(a => a.SurveyId === surveyId));

            this.closePanelEditor();
        }
        else if (CreatePanelCommandV1.matchType(cmd))
        {
            const name = correlationId;
            this.runPanelByNameQueryV1(this.workspaceId, this.EvalModelId, name);

            this.closePanelEditor();
        }
        else if (UpdatePanelCommandV1.matchType(cmd))
        {
            const surveyId = Number.parseInt(correlationId);
            this.runPanelQueryV1(this.workspaceId, surveyId);

            this.closePanelEditor();
        }
        else if (UpdatePanelAlternativesSettingsCommandV1.matchType(cmd))
        {
            const surveyId = Number.parseInt(correlationId);
            this.runPanelQueryV1(this.workspaceId, surveyId);
        }
        else if (UpdatePanelAttributesSettingsCommandV1.matchType(cmd))
        {
            const surveyId = Number.parseInt(correlationId);
            this.runPanelQueryV1(this.workspaceId, surveyId);

            this.closeAttributesManager();
        }
        else if (OpenPanelAccessCommandV1.matchType(cmd))
        {
            const surveyId = Number.parseInt(correlationId);
            const s = this.Surveys.find(a => a.SurveyId === surveyId);
            s.SurveyStarted = true;
            this.panelOpener.close();
        }
        else if (ClosePanelAccessCommandV1.matchType(cmd))
        {
            const surveyId = Number.parseInt(correlationId);
            const s = this.Surveys.find(a => a.SurveyId === surveyId);
            s.SurveyStarted = false;
            this.panelCloser.close();
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (CreatePanelCommandV1.matchType(status.Command))
        {
            this.panelEditor.displayValidationErrors(status.ValidationErrors);
        }
        else if (UpdatePanelCommandV1.matchType(status.Command))
        {
            this.panelEditor.displayValidationErrors(status.ValidationErrors);
        }
    }
}


class PanelEditorProps
{
    SurveyId: number = null;
    Name: string = null;
    Description: string = null;
    Members: MemberReadModel[] = null;
    OwnerEmail: string = null;
    NameError: string = null;
    ResponseDueDate: string | null;

    constructor(surveyId: number, name: string, description: string, members: MemberReadModel[], ownerEmail: string, responseDueDate: string | null)
    {
        this.SurveyId = surveyId;
        this.Name = name;
        this.Description = description;
        this.Members = members;
        this.OwnerEmail = ownerEmail;
        this.ResponseDueDate = responseDueDate;
    }

    displayValidationErrors(errors: ValidationError[])
    {
        for (const error of errors)
        {
            if (error.Context == "Name")
            {
                this.NameError = error.Error;
            }
        }
    }

    clearValidationErrors()
    {
        this.NameError = null;
    }
}

class AttributesSelectorProps
{
    CorrelationId: string;
    Attributes: { AttributeId: number, Name: string, Section: string }[];
    SelectedAttributesIds: number[];

    constructor(correlationId: string, selectedAttributesIds: number[], attributes: { AttributeId: number, Name: string, Section: string }[])
    {
        this.CorrelationId = correlationId;
        this.Attributes = attributes;
        this.SelectedAttributesIds = selectedAttributesIds;
    }
}